/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { useVideoContext } from '../../contexts/videoProvider/videoContext';
import { isEmpty } from '@utils/lodash';

const upsertParticipantAndKeepOrder = (
  participants: VideoProvider.IParticipant[],
  newParticipant: VideoProvider.IParticipant,
) => {
  const index = participants.findIndex(p => p.sid === newParticipant.sid);
  const updated = participants.filter(p => p.sid !== newParticipant.sid);
  if (index >= 0) {
    updated.splice(index, 0, newParticipant);
  } else {
    updated.push(newParticipant);
  }
  return updated;
};

export function useParticipants() {
  const { room, videoProvider } = useVideoContext();
  const [participants, setParticipants] = useState<VideoProvider.IParticipant[]>(videoProvider.getParticipants());

  useEffect(() => {
    if (!isEmpty(room) && room?.on && room?.off) {
      setParticipants(Array.from(room?.participants?.values() || []));
      // we only want to run this effect if the room exists
      const participantConnected = (participant: VideoProvider.IParticipant) => {
        /* eslint-disable no-console */
        console.info(`Twilio room event: participantConnected. SID=${participant.sid}. Participant=`, participant);
        setParticipants(prevParticipants => upsertParticipantAndKeepOrder(prevParticipants, participant));
      };
      const participantReconnected = (participant: VideoProvider.IParticipant) => {
        /* eslint-disable no-console */
        console.info(`Twilio room event: participantReconnected. SID=${participant.sid}. Participant=`, participant);
        setParticipants(prevParticipants => upsertParticipantAndKeepOrder(prevParticipants, participant));
      };
      const participantDisconnected = (participant: VideoProvider.IParticipant) => {
        /* eslint-disable no-console */
        console.info(`Twilio room event: participantDisconnected. SID=${participant.sid}. Participant=`, participant);
        setParticipants(prevParticipants => prevParticipants.filter(p => p.sid !== participant.sid));
      };

      // These events are only generated for RemoteParticipants, not for local participant
      room.on?.('participantConnected', participantConnected);
      room.on?.('participantReconnected', participantReconnected);
      room.on?.('participantDisconnected', participantDisconnected);
      return () => {
        room.off?.('participantConnected', participantConnected);
        room.off?.('participantReconnected', participantReconnected);
        room.off?.('participantDisconnected', participantDisconnected);
      };
    }
    return () => {};
  }, [room]);

  return participants;
}
